/**
 * The AI-administration domain: thinkers, capability policies and AI actions.
 *
 * These live beside `types.ts` rather than inside it for the same reason `knowitWork.ts` keeps
 * the Work shapes to itself — they translate a *different* wire convention. The document
 * surface is Outline-style RPC with a `{data, policies}` envelope; every endpoint behind this
 * file answers a bare object or array, except `selection-actions`, which uses a `{data}`
 * wrapper and nothing else. Keeping the conventions apart is what stops the envelope helpers
 * from being quietly wrong for half the app.
 *
 * Schemas are zod so the golden-fixture test can assert the parsed shape rather than a
 * hand-written sample of it, exactly as `AgentSchema` and friends do next door.
 */
import { z } from "zod";

// ── Thinkers ─────────────────────────────────────────────────────────────────

/**
 * Skill capability profiles a thinker can be assigned. Mirrors the backend's `SkillCatalog`
 * templates; the server is the authority and validates, so this list is for the picker only.
 */
export const SKILL_TEMPLATES = [
  "author",
  "read_only",
  "conversational",
  "researcher",
  "orchestrator",
  "full_power",
] as const;
export type SkillTemplate = (typeof SKILL_TEMPLATES)[number];

/** Engines a local thinker profile can be pinned to. */
export const THINKER_PROVIDERS = ["ollama", "mlx", "openai", "anthropic"] as const;
export type ThinkerProvider = (typeof THINKER_PROVIDERS)[number];

/** Where a Connected-IDE thinker entry comes from. Display-only; nothing is verified here. */
export const IdeThinkerSourceSchema = z.object({
  ownerUserName: z.string().nullable(),
  machineId: z.string().nullable(),
  machineLabel: z.string().nullable(),
  workspaceName: z.string().nullable(),
  workspacePath: z.string().nullable(),
  extensionVersion: z.string().nullable(),
  isOnline: z.boolean(),
});
export type IdeThinkerSource = z.infer<typeof IdeThinkerSourceSchema>;

/**
 * One entry in the workspace's selectable set — a cloud persona, a local model, a human
 * assistant, or (behind the server's flag) a connected IDE window. This is the authoritative
 * answer to "who can this workspace address", which is why the picker sources from it.
 */
export const AvailableThinkerSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  isLocal: z.boolean(),
  isHuman: z.boolean(),
  /** True for anything the engine can actually run — never inferred from `isHuman`. */
  isAi: z.boolean(),
  provider: z.string(),
  avatarUrl: z.string().nullable(),
  areaTags: z.array(z.string()),
  category: z.string().nullable(),
  icon: z.string().nullable(),
  /**
   * False for a persona shared view-only: visible, not selectable. Rendering it as absent
   * would look like a bug; rendering it as available would fail at send-time.
   */
  usable: z.boolean(),
  /** `"ide"` for a Connected-IDE window; null for every entry that predates the field. */
  kind: z.string().nullable(),
  source: IdeThinkerSourceSchema.nullable(),
  /** The model a persona answers with (its binding, else its default). Null when the server does not say. */
  model: z.string().nullable().optional(),
});
export type AvailableThinker = z.infer<typeof AvailableThinkerSchema>;

/**
 * A persona's whole record as the curation UI sees it — the fields KnowIt used to discard at
 * the TomatoFlow boundary and now owns. `added` is this workspace's tenancy row; `owned` means
 * a workspace has claimed the record by editing it here.
 */
export const CatalogThinkerSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  category: z.string().nullable(),
  subcategory: z.string().nullable(),
  icon: z.string().nullable(),
  department: z.string().nullable(),
  personalityFocus: z.string().nullable(),
  personalityPrompt: z.string().nullable(),
  added: z.boolean(),
  /** Engine binding for THIS workspace; null means the server default. */
  provider: z.string().nullable(),
  model: z.string().nullable(),
  skillTemplate: z.string().nullable(),
  isAi: z.boolean(),
  isExtensionVisible: z.boolean(),
  decisionThreshold: z.number(),
  temperature: z.number().nullable(),
  maxOutputTokens: z.number().nullable(),
  defaultConfidence: z.number().nullable(),
  backendKind: z.string().nullable(),
  billingModel: z.string().nullable(),
  billingLabel: z.string().nullable(),
  hrTraits: z.record(z.string(), z.number()),
  tools: z.array(z.string()),
  presetMemory: z.array(z.string()),
  validatedBy: z.array(z.string()),
  validates: z.array(z.string()),
  isValidationThinker: z.boolean(),
  /** tomatoflow (imported) | builtin (code seed) | knowit (authored or claimed here). */
  origin: z.string(),
  owned: z.boolean(),
});
export type CatalogThinker = z.infer<typeof CatalogThinkerSchema>;

/** The narrow shape the create/update routes answer with. */
export const ThinkerRecordSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  isAi: z.boolean(),
  category: z.string().nullable(),
  icon: z.string().nullable(),
  personalityPrompt: z.string().nullable(),
  enabled: z.boolean(),
  origin: z.string(),
});
export type ThinkerRecord = z.infer<typeof ThinkerRecordSchema>;

/**
 * Health of the persona catalog. Kept even though the answer is now structurally `ok`: the
 * curation UI must never mistake an empty picker for an outage.
 */
export const CatalogStatusSchema = z.object({
  status: z.string(),
  lastError: z.string().nullable(),
  lastRefreshAt: z.string().nullable(),
  count: z.number(),
});
export type CatalogStatus = z.infer<typeof CatalogStatusSchema>;

/** What seeding the built-in specialists actually did. Idempotent; never overwrites an edit. */
export const PersonaImportResultSchema = z.object({
  builtInsSeeded: z.number(),
  imported: z.number(),
  updated: z.number(),
  skipped: z.number(),
  total: z.number(),
  error: z.string().nullable(),
});
export type PersonaImportResult = z.infer<typeof PersonaImportResultSchema>;

export const THINKER_SHARE_ROLES = ["use", "view"] as const;
export type ThinkerShareRole = (typeof THINKER_SHARE_ROLES)[number];

/** An active grant this workspace issued for one of its thinkers. */
export const ThinkerShareSchema = z.object({
  id: z.string(),
  thinkerId: z.string(),
  kind: z.string(),
  recipientTeamId: z.string().nullable(),
  recipientUserId: z.string().nullable(),
  recipientGroupId: z.string().nullable(),
  expiresAt: z.string().nullable(),
  role: z.string(),
});
export type ThinkerShare = z.infer<typeof ThinkerShareSchema>;

export const GroupOptionSchema = z.object({ id: z.string(), name: z.string() });
export type GroupOption = z.infer<typeof GroupOptionSchema>;

/** One synchronous server-persona call. Field names are the published adapter contract. */
export const ThinkerInvocationSchema = z.object({
  thinkerId: z.string(),
  result: z.string(),
  conversationId: z.string().nullable(),
  model: z.string().nullable(),
  inputTokens: z.number(),
  outputTokens: z.number(),
});
export type ThinkerInvocation = z.infer<typeof ThinkerInvocationSchema>;

// ── Local thinkers ───────────────────────────────────────────────────────────

/** A model the workspace runs on hardware it controls (or a pinned cloud model). */
export const LocalThinkerSchema = z.object({
  capabilities: z.record(z.string(), z.unknown()).nullable().optional(),
  installation: z.record(z.string(), z.unknown()).nullable().optional(),
  /** The row's guid — what update and delete address. */
  id: z.string(),
  /** The stable slug conversations and flow nodes resolve by. */
  thinkerId: z.string(),
  displayName: z.string(),
  provider: z.string(),
  /** `standalone` (its own pickable thinker) or `persona` (an engine binding for one). */
  kind: z.string(),
  baseUrl: z.string(),
  model: z.string(),
  systemPrompt: z.string().nullable(),
  temperature: z.number().nullable(),
  maxOutputTokens: z.number(),
  enabled: z.boolean(),
  skillTemplate: z.string().nullable(),
  webMaxSourcesPerRun: z.number().nullable(),
  webMaxCrawlDepth: z.number().nullable(),
  webAllowedEngines: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type LocalThinker = z.infer<typeof LocalThinkerSchema>;

export const OllamaModelSchema = z.object({
  name: z.string(),
  size: z.number().nullable(),
  parameterSize: z.string().nullable(),
});
export type OllamaModel = z.infer<typeof OllamaModelSchema>;

/**
 * The installed-model probe. Unreachable is *data*, not an exception — the server answers 200
 * with `reachable:false`, because "Ollama is not running" is an answer and "something went
 * wrong" is not.
 */
export const LocalThinkerModelsSchema = z.object({
  reachable: z.boolean(),
  error: z.string().nullable(),
  models: z.array(OllamaModelSchema),
});
export type LocalThinkerModels = z.infer<typeof LocalThinkerModelsSchema>;

/** Server-side health, which separates "host down" from "model not installed". */
export const LocalThinkerHealthSchema = z.object({
  reachable: z.boolean(),
  models: z.array(z.string()),
  /** Null unless a model was named — an unasked question has no answer. */
  modelInstalled: z.boolean().nullable(),
  error: z.string().nullable(),
});
export type LocalThinkerHealth = z.infer<typeof LocalThinkerHealthSchema>;

export const LocalThinkerTestSchema = z.object({
  reachable: z.boolean(),
  modelCount: z.number(),
  models: z.array(z.string()),
  error: z.string().nullable(),
});
export type LocalThinkerTest = z.infer<typeof LocalThinkerTestSchema>;

/**
 * One single-shot local invocation. The two honest limits ride along rather than being hidden:
 * a local thinker has no tools, and Ollama silently truncates the prompt at its own ceiling.
 */
export const LocalThinkerInvocationSchema = z.object({
  ok: z.boolean(),
  thinkerId: z.string(),
  model: z.string(),
  text: z.string(),
  tokensUsed: z.number(),
  durationMs: z.number(),
  hasTools: z.boolean(),
  promptCeilingExceeded: z.boolean(),
  promptCeilingTokens: z.number(),
  approxPromptTokens: z.number(),
  error: z.string().nullable(),
  message: z.string().nullable(),
});
export type LocalThinkerInvocation = z.infer<typeof LocalThinkerInvocationSchema>;

// ── Thinker persona: facets ──────────────────────────────────────────────────

/** How a facet acts when it is relevant. Mirrors the server's `ThinkerFacet.Pursuits`. */
export const THINKER_PURSUITS = [
  "question",
  "propose",
  "verify",
  "protect",
  "simplify",
  "connect",
] as const;
export type ThinkerPursuit = (typeof THINKER_PURSUITS)[number];

/** A thinker has at most this many facets (server rule, `PersonaBlender.Validate`). */
export const MAX_THINKER_FACETS = 8;

/**
 * The work library — the 14 facets `GET thinkers/facets/library` serves (`ThinkerFacetLibrary.cs`,
 * owner 2026-10-10: "a list of traits relevant to work"). The fake answers exactly these; the
 * editor offers them prefilled.
 */
export const THINKER_FACET_LIBRARY: readonly ThinkerFacet[] = [
  {
    id: "builder",
    name: "Builder",
    intent: "Turn talk into the smallest thing that can be built and tried.",
    interests: ["implementation", "scope", "sequencing", "deliverable", "acceptance test"],
    toward: "One bounded piece of work with a test that proves it",
    amplitude: 1,
    pursuit: "propose",
    voice: "Practical; speaks in steps and deliverables.",
  },
  {
    id: "architect",
    name: "Architect",
    intent: "Keep the whole coherent: boundaries, contracts, and what else moves when this moves.",
    interests: ["architecture", "interfaces", "coupling", "data model", "consistency"],
    toward: "A change that fits the existing shape or reshapes it on purpose",
    amplitude: 1,
    pursuit: "connect",
    voice: "Draws the boundary before the detail.",
  },
  {
    id: "tester",
    name: "Tester",
    intent: "Prove it works before believing it works.",
    interests: ["tests", "evidence", "edge cases", "regression", "reproduction"],
    toward: "A claim backed by a test that would fail if it were false",
    amplitude: 1,
    pursuit: "verify",
    voice: "Asks 'how do we know?' and then checks.",
  },
  {
    id: "skeptic",
    name: "Skeptic",
    intent: "Find the claim that is doing the most work with the least evidence.",
    interests: ["assumptions", "counterexamples", "unverified claims", "failure modes"],
    toward: "A conclusion that survives its strongest objection",
    amplitude: 1,
    pursuit: "question",
    voice: "Terse; names the weakest link first.",
  },
  {
    id: "risk-watcher",
    name: "Risk watcher",
    intent: "Name what can go wrong, how badly, and whether anyone would notice.",
    interests: ["failure", "data loss", "security", "rollback", "blast radius", "detection"],
    toward: "A plan whose worst case is survivable and observable",
    amplitude: 1,
    pursuit: "protect",
    voice: "Calm, specific about consequences.",
  },
  {
    id: "cost-watcher",
    name: "Cost watcher",
    intent: "Make the price of each option explicit before anyone commits.",
    interests: ["cost", "time", "tokens", "compute", "maintenance", "opportunity cost"],
    toward: "The option whose benefit clearly exceeds its written-down cost",
    amplitude: 1,
    pursuit: "verify",
    voice: "Numbers first, adjectives last.",
  },
  {
    id: "user-advocate",
    name: "User advocate",
    intent: "Keep the person who will live with the outcome in the room.",
    interests: ["user experience", "clarity", "effort for the user", "trust", "accessibility"],
    toward: "An outcome a non-expert can understand and act on",
    amplitude: 1,
    pursuit: "connect",
    voice: "Plain words; asks 'and then what happens to them?'",
  },
  {
    id: "finisher",
    name: "Finisher",
    intent: "Close the loop: ship it, verify it, write it down.",
    interests: ["done criteria", "deployment", "documentation", "follow-through", "loose ends"],
    toward: "Nothing half-done; every change proven live and recorded",
    amplitude: 1,
    pursuit: "simplify",
    voice: "Lists what is still open until nothing is.",
  },
  {
    id: "simplifier",
    name: "Simplifier",
    intent: "Remove what is not needed; fewer moving parts.",
    interests: ["simplicity", "scope cut", "defaults", "fewer concepts"],
    toward: "The smallest design that does the job",
    amplitude: 1,
    pursuit: "simplify",
    voice: "Asks what can be left out.",
  },
  {
    id: "explorer",
    name: "Explorer",
    intent: "Look for the option nobody has named yet.",
    interests: ["alternatives", "prior art", "experiments", "what if"],
    toward: "A better option found before committing to the first",
    amplitude: 1,
    pursuit: "question",
    voice: "Curious; offers a second path.",
  },
  {
    id: "planner",
    name: "Planner",
    intent: "Order the work so each step is safe and teaches something.",
    interests: ["dependencies", "sequencing", "milestones", "risk first", "parallel work"],
    toward: "A sequence where the riskiest unknown is tested first",
    amplitude: 1,
    pursuit: "propose",
    voice: "Thinks in order and in dependencies.",
  },
  {
    id: "mentor",
    name: "Mentor",
    intent: "Explain so the next person can act without you.",
    interests: ["clarity", "rationale", "handover", "teaching", "naming"],
    toward: "A decision whose reasons are written where the next reader will look",
    amplitude: 1,
    pursuit: "connect",
    voice: "Patient; says why, not just what.",
  },
  {
    id: "operator",
    name: "Operator",
    intent: "Keep production alive while changing it.",
    interests: ["deploy", "monitoring", "rollback", "capacity", "incident"],
    toward: "A change that can be shipped, watched and undone",
    amplitude: 1,
    pursuit: "protect",
    voice: "Thinks about 3 a.m.",
  },
  {
    id: "historian",
    name: "Historian",
    intent: "Remember what was tried before and what it taught.",
    interests: ["precedent", "lessons", "past attempts", "what already exists"],
    toward: "Not repeating a known failure; reusing a known success",
    amplitude: 1,
    pursuit: "question",
    voice: "Tells the short version of last time.",
  },
];

/**
 * One facet of a thinker's personality: a role inside its own thought process with its own
 * intent and interests. A thinker blends its facets by the situation it is in — the blend is
 * deterministic and lexical, so a reader can see WHY it leaned the way it did.
 */
export const ThinkerFacetSchema = z.object({
  /** Stable slug; blank on a new facet and the server derives it from the name. */
  id: z.string(),
  name: z.string(),
  /** What this facet wants in any exchange ("make it shippable"). */
  intent: z.string(),
  /** Topics it attends to first. Up to 12. */
  interests: z.array(z.string()),
  /** The outcome it pushes for. */
  toward: z.string().nullable(),
  /** 0..1 — how loudly it speaks when it is relevant; in a hive, its VOTE weight. */
  amplitude: z.number(),
  pursuit: z.string(),
  /** One sentence of tone. */
  voice: z.string().nullable(),
  /** The facet that decides for the thinker after the hive has voted. At most one per thinker. */
  authority: z.boolean().optional(),
  /** BRONCO Opus 03 — who plays the facet's seat: a role or a capability (preferred), or a pinned thinker. */
  binding: z
    .object({
      role: z.string().nullable().optional(),
      capability: z.string().nullable().optional(),
      thinkerId: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  /** The room acts it may write; absent = whatever the phase asks. */
  acts: z.array(z.string()).nullable().optional(),
  /** How much of the board it reads; absent = the room's budget. */
  sliceChars: z.number().nullable().optional(),
  enabled: z.boolean().optional(),
});
export type ThinkerFacet = z.infer<typeof ThinkerFacetSchema>;

/** BRONCO Opus 03 — the room acts a facet may be allowed to write. */
export const THINKER_FACET_ACTS = [
  "simplify",
  "question",
  "answer",
  "check",
  "link",
  "merge",
  "settle",
] as const;

// ── The hive: a thinker's facets thinking the same thing at once ─────────────────────────────
// Owner (2026-10-10): "thinkers that are a hive mind thinking of the same thing at once — in
// turns or in parallel — each personality attached assesses the prompt and responds with its own
// point of view. After all facets have answered, the thinker decides — by the personality marked
// as the authority — where the personalities have weight (an internal voting system). The output
// is amalgamated from all the opinions. Inside, the facets debate; each impasse asks all other
// facets; if it takes too long it is an impasse, and a human is involved with all the relevant
// impasses at the end."

export type ThinkerHiveMode = "parallel" | "turns";
export interface ThinkerHivePolicy {
  enabled: boolean;
  /** `parallel`: all facets at once; `turns`: each sees the earlier ones. */
  mode: ThinkerHiveMode;
  /** 0–4, default 2. */
  maxDebateRounds: number;
  /** 0–6, default 3 — impasses per thought. */
  maxImpasses: number;
  /** 40–400, default 120 — words per facet. */
  facetWords: number;
}
export const DEFAULT_HIVE_POLICY: ThinkerHivePolicy = {
  enabled: false,
  mode: "parallel",
  maxDebateRounds: 2,
  maxImpasses: 3,
  facetWords: 120,
};
/** The cost of one thought: every facet answers, each impasse asks the others, the authority decides. */
export const hiveCalls = (facets: number, policy: ThinkerHivePolicy) =>
  facets + policy.maxImpasses * Math.max(0, facets - 2) * policy.maxDebateRounds + 1;

export interface HiveObjection {
  /** The facet objected to (its id). */
  to: string;
  claim: string;
  why: string;
}
/** One facet's answer inside the hive. */
export interface HiveView {
  facetId: string;
  name: string;
  weight: number;
  position: string;
  view: string;
  /** 0..1 */
  confidence: number;
  objections: HiveObjection[];
  valid: boolean;
}
export interface HiveVote {
  facetId: string;
  name: string;
  weight: number;
  /** The facet id voted for. */
  for: string;
  why: string;
}
export interface HiveImpasse {
  id: string;
  /** Facet ids on each side. */
  from: string;
  to: string;
  claim: string;
  why: string;
  votes: HiveVote[];
  status: "resolved" | "unresolved" | "ruled";
  resolution: string | null;
  /** Vote weight on each side. */
  forFrom: number;
  forTo: number;
  rounds: number;
}
export interface HiveDecision {
  authorityId: string;
  authorityName: string;
  /** The amalgamated answer. */
  answer: string;
  tookFrom: string[];
  dissent: string[];
}
/** THE THOUGHT: what happened inside a thinker for one prompt. */
export interface HiveThought {
  views: HiveView[];
  impasses: HiveImpasse[];
  decision: HiveDecision | null;
  unresolved: HiveImpasse[];
  calls: number;
  durationMs: number;
  policy: ThinkerHivePolicy | null;
}
/** Your call on an impasse the hive could not settle: a facet id, or "neither". */
export interface HiveRuling {
  impasseId: string;
  for: string;
  note?: string;
}

/** One facet's share of a blend, with the terms it has in common with the situation. */
export const ThinkerFacetWeightSchema = z.object({
  id: z.string(),
  name: z.string(),
  /** 0..1, rounded to two places; the weights sum to 1. */
  weight: z.number(),
  shared: z.array(z.string()),
});
export type ThinkerFacetWeight = z.infer<typeof ThinkerFacetWeightSchema>;

/** The dry-run answer: how the facets blend for a situation, and the persona block that renders. */
export const ThinkerBlendPreviewSchema = z.object({
  weights: z.array(ThinkerFacetWeightSchema),
  prompt: z.string(),
});
export type ThinkerBlendPreview = z.infer<typeof ThinkerBlendPreviewSchema>;

/** What you ask a thinker when you TRY it (`POST local-thinkers/{id}/try`, `POST thinkers/personas/{id}/try`). */
export interface ThinkerTryInput {
  /** ≤ 2000 characters. */
  prompt: string;
  interests?: string[];
  toward?: string;
  maxOutputTokens?: number;
  /** null/undefined = the stored hive policy decides; true forces the hive; false forces one blended call. */
  hive?: boolean | null;
  /** Your rulings on earlier impasses, for a re-run. */
  rulings?: HiveRuling[];
}
/** One facet's share of the blend the thinker answered from; `semantic` when embeddings took part. */
export interface ThinkerTryWeight extends ThinkerFacetWeight {
  semantic: number | null;
}
export interface ThinkerTryBlend {
  weights: ThinkerTryWeight[];
  /** `lexical` or `lexical+semantic`. */
  method: string;
  prompt: string;
}
/** ONE real completion as the thinker, its facet blend prepended — exactly what a research seat gets. */
export interface ThinkerTryResult {
  text: string;
  reasoning: string | null;
  model: string;
  durationMs: number;
  inputTokens: number;
  outputTokens: number;
  /** Local thinkers only. */
  systemPrompt: string | null;
  /** null when the thinker has no facets. */
  blend: ThinkerTryBlend | null;
  /** THE THOUGHT when the hive was on; `text` is then the amalgamated answer. */
  hive: HiveThought | null;
}

// ── Capability policies (AI permissions) ─────────────────────────────────────

export const CAPABILITY_EFFECTS = ["allow", "deny"] as const;
export type CapabilityEffect = (typeof CAPABILITY_EFFECTS)[number];

/**
 * One RBAC rule over conversation capabilities. The default posture is allow-everything except
 * the locked `system` class, so a rule here either restricts (deny) or extends (allow).
 */
export const CapabilityPolicySchema = z.object({
  id: z.string(),
  scope: z.string(),
  scopeId: z.string().nullable(),
  principalKind: z.string(),
  principalId: z.string(),
  permissionKey: z.string(),
  effect: z.string(),
  createdBy: z.string().nullable(),
  createdAt: z.string(),
});
export type CapabilityPolicy = z.infer<typeof CapabilityPolicySchema>;

export const CapabilitySkillSchema = z.object({
  permissionKey: z.string(),
  id: z.string(),
  name: z.string(),
  /** none | read | write | system — how much damage the skill can do. */
  sideEffect: z.string(),
});
export type CapabilitySkill = z.infer<typeof CapabilitySkillSchema>;

/** What a policy may target, so the UI offers pickers instead of free text. */
export const CapabilityCatalogSchema = z.object({
  skills: z.array(CapabilitySkillSchema),
  wildcards: z.array(z.string()),
  principalKinds: z.array(z.string()),
  thinkerTemplates: z.array(z.string()),
  effects: z.array(z.string()),
  /** The server's own sentence about the default posture. Shown, never paraphrased. */
  note: z.string(),
});
export type CapabilityCatalog = z.infer<typeof CapabilityCatalogSchema>;

// ── AI actions (selection actions) ───────────────────────────────────────────

/** A prompt-backed transformation offered where text is selected. */
export const SelectionActionSchema = z.object({
  id: z.string(),
  title: z.string(),
  icon: z.string(),
  prompt: z.string(),
  shortcut: z.string().nullable(),
  showInMenu: z.boolean(),
  sortOrder: z.number(),
  /** System | User | Team | Project | Document — the server's own word. */
  scope: z.string(),
  /** Built-ins can be hidden from the menu but never edited or deleted. */
  isBuiltIn: z.boolean(),
});
export type SelectionAction = z.infer<typeof SelectionActionSchema>;

/**
 * Whether an AI provider is configured and reachable.
 *
 * `configured:false` is reported ONLY for an explicit `provider_not_configured`. Every
 * ambiguous outcome — an old backend's 404, a 401, a transport failure — reports configured,
 * because a false "configure your provider" gate is worse than no gate at all.
 */
export const AiProviderStatusSchema = z.object({
  configured: z.boolean(),
  reachable: z.boolean(),
  provider: z.string().nullable(),
});
export type AiProviderStatus = z.infer<typeof AiProviderStatusSchema>;
